import React, { useEffect, useState } from "react";
import { api, errorMessage } from "../../lib/api";
import { toast } from "sonner";
import { Gavel, Plus, Trash2 } from "lucide-react";

export default function JudgesPanel() {
  const [judges, setJudges] = useState([]);
  const [form, setForm] = useState({ name: "", email: "", round: 1 });
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try { const { data } = await api.get("/admin/judges"); setJudges(data.judges); } catch (err) { toast.error(errorMessage(err)); }
  };
  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post("/admin/judges", { ...form, round: parseInt(form.round) });
      toast.success("Judge added — they'll log in via OTP");
      setForm({ name: "", email: "", round: 1 });
      await load();
    } catch (err) { toast.error(errorMessage(err)); } finally { setBusy(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Remove this judge and all their scores?")) return;
    try { await api.delete(`/admin/judges/${id}`); toast.success("Judge removed"); await load(); }
    catch (err) { toast.error(errorMessage(err)); }
  };

  return (
    <div className="grid lg:grid-cols-5 gap-6" data-testid="judges-panel">
      <form onSubmit={submit} className="lg:col-span-2 card-surface">
        <div className="flex items-center gap-2 mb-4">
          <Gavel className="w-5 h-5 text-brand" /><div className="font-display text-xl text-white">Add Judge</div>
        </div>
        <div className="space-y-3">
          <div><label className="label-mono block mb-1">Name</label>
            <input value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required className="input-field" data-testid="judge-form-name" /></div>
          <div><label className="label-mono block mb-1">Email</label>
            <input type="email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} required className="input-field" placeholder="any email — gmail, yahoo, college, etc." data-testid="judge-form-email" />
            <p className="text-xs text-gray-500 mt-1.5 font-mono">// No password needed — judge will receive a one-time code by email at sign-in</p>
          </div>
          <div><label className="label-mono block mb-1">Assigned Round</label>
            <select value={form.round} onChange={e => setForm({ ...form, round: e.target.value })} className="input-field" data-testid="judge-form-round">
              <option value={1}>Round 1</option><option value={2}>Round 2</option>
            </select></div>
          <button type="submit" disabled={busy} className="btn-primary w-full flex items-center justify-center gap-2" data-testid="add-judge-btn">
            {busy ? <span className="spinner" /> : <Plus className="w-4 h-4" />} Add Judge
          </button>
        </div>
      </form>

      <div className="lg:col-span-3 card-surface overflow-x-auto" data-testid="judges-table">
        <div className="label-mono mb-3">Active Judges</div>
        <table className="w-full text-sm">
          <thead>
            <tr className="label-mono text-left border-b border-white/10">
              <th className="py-3 pr-4">Name</th><th className="py-3 pr-4">Email</th>
              <th className="py-3 pr-4">Round</th><th className="py-3 pr-4">Progress</th><th className="py-3 pr-4"></th>
            </tr>
          </thead>
          <tbody>
            {judges.map(j => {
              const rounds = j.rounds || (j.round ? [j.round] : []);
              const missingRound = [1, 2].find(r => !rounds.includes(r));
              return (
              <tr key={j.id} className="border-b border-white/5 hover:bg-white/[0.02]" data-testid={`judge-row-${j.id}`}>
                <td className="py-3 pr-4 text-white">{j.name}</td>
                <td className="py-3 pr-4 text-gray-400">{j.email}</td>
                <td className="py-3 pr-4">
                  <div className="flex flex-wrap gap-1.5">
                    {rounds.length === 0 && <span className="badge badge-upcoming">—</span>}
                    {rounds.map(r => (
                      <span key={r} className="badge badge-accepted" data-testid={`judge-${j.id}-round-${r}`}>R{r}</span>
                    ))}
                    {missingRound && (
                      <button
                        onClick={async () => {
                          try {
                            await api.post(`/admin/judges/${j.id}/assign-round`, { round: missingRound });
                            toast.success(`Also assigned to Round ${missingRound}`);
                            await load();
                          } catch (err) { toast.error(errorMessage(err)); }
                        }}
                        className="text-[10px] font-mono uppercase tracking-widest text-brand hover:text-white border border-brand/40 hover:border-brand px-2 py-0.5"
                        data-testid={`add-round-${j.id}-${missingRound}`}
                        title={`Also assign this judge to Round ${missingRound}`}
                      >
                        + R{missingRound}
                      </button>
                    )}
                  </div>
                </td>
                <td className="py-3 pr-4 font-mono text-xs">{j.scored} / {j.total}</td>
                <td className="py-3 pr-4">
                  <button onClick={() => remove(j.id)} className="text-gray-500 hover:text-[#EF4444]" data-testid={`remove-judge-${j.id}`}>
                    <Trash2 className="w-4 h-4" />
                  </button>
                </td>
              </tr>
            );})}
            {judges.length === 0 && <tr><td colSpan={5} className="text-center text-gray-500 py-8">No judges added yet.</td></tr>}
          </tbody>
        </table>
      </div>
    </div>
  );
}
